import { AppConstants } from '@/helpers/app.constants';
import { DictionaryHelper } from '@/helpers/dictionary.helper';
import { PropertyHelper } from '@/helpers/property.helper';
import { AlertService } from '@/services/alert.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { Attribute, AttributeGroup, AttributeType, DataDictionaryService, PropertyCategory } from '@eemo/api-client';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { InputNullValueDirective } from '../../extensions/inputNullValueDirective';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { NgIf, NgFor } from '@angular/common';
import { InputTrimDirective } from '../../extensions/inputTrimDirective';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

export class AttributeDialodData {
  attr: Attribute;
  edition = false;
}

@Component({
    selector: 'app-attribute-dialog',
    templateUrl: './attribute-dialog.component.html',
    styleUrls: ['./attribute-dialog.component.scss'],
    standalone: true,
    imports: [MatDialogModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, InputTrimDirective, NgIf, MatSelectModule, NgFor, MatOptionModule, MatSlideToggleModule, MatTooltipModule, MatIconModule, InputNullValueDirective, MatDividerModule, MatButtonModule]
})
export class AttributeDialogComponent implements OnInit {
  attrForm: UntypedFormGroup;
  formSubmitting = false;
  groups: AttributeGroup[];
  groupsByCategory: { [key: string]: AttributeGroup[] } = {};
  dialogTitle: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AttributeDialodData,
    private dictService: DataDictionaryService,
    private fb: UntypedFormBuilder,
    private alertService: AlertService,
    public dialogRef: MatDialogRef<AttributeDialogComponent>,
    public propHelper: PropertyHelper,
    public dictHelper: DictionaryHelper
  ) {
    this.dialogTitle = this.data.edition ? "Modifier l'attribut" : 'Créer un nouvel attribut';
  }

  // convenience getter for easy access to form fields
  f(field: string): AbstractControl {
    return this.attrForm.get(field);
  }

  ngOnInit() {
    this.attrForm = this.fb.group({
      code: ['', [Validators.required, Validators.pattern(AppConstants.FORM_PATTERN_CODE)]],
      type: [AttributeType.STRING, Validators.required],
      category: [PropertyCategory.BUILDING, Validators.required],
      groupCode: ['', Validators.required],
      required: [false],
      summary: [false],
      unit: ['', Validators.maxLength(15)],
      inCertification: [false],
      rescue: [false],
      allowedPropertyTypes: [[]],
      availableValues: this.fb.array([]),
      display: this.fb.group({
        name: ['', [Validators.required, Validators.maxLength(50)]],
        description: ['', Validators.maxLength(160)],
        order: [null, Validators.pattern(AppConstants.FORM_PATTERN_INTEGER)]
      })
    });
    this.f('type').valueChanges.subscribe((type) => {
      this.onTypeChanged();
    });

    if (this.data.attr) {
      if (this.data.attr.availableValues) {
        this.data.attr.availableValues.forEach((value) =>
          this.availableValues.push(this.createAvailableValueFormItem())
        );
      }
      this.attrForm.patchValue(this.data.attr);
      this.onTypeChanged();
    }

    this.dictService.getDictionaryGroups().subscribe((groups) => {
      this.groups = groups;
      groups.forEach((group) => {
        if (!this.groupsByCategory[group.category]) {
          this.groupsByCategory[group.category] = [];
        }
        this.groupsByCategory[group.category].push(group);
      });
    });
  }

  updateGroupList() {
    this.f('group').updateValueAndValidity();
  }

  get availableValues(): UntypedFormArray {
    return this.attrForm.get('availableValues') as UntypedFormArray;
  }

  isTypeEnum(): boolean {
    return this.f('type').value === AttributeType.ENUM;
  }

  isTypeNumber(): boolean {
    const type = this.f('type').value;
    return type === AttributeType.INTEGER || type === AttributeType.DECIMAL;
  }

  onTypeChanged() {
    if (this.isTypeNumber()) {
      this.f('unit').enable();
    } else {
      this.f('unit').disable();
    }
  }

  createAvailableValueFormItem() {
    return this.fb.group({
      code: ['', [Validators.required, Validators.pattern(AppConstants.FORM_PATTERN_CODE)]],
      display: this.fb.group({
        name: ['', [Validators.required, Validators.maxLength(50)]],
        description: ['', Validators.maxLength(160)],
        order: [null, Validators.pattern(AppConstants.FORM_PATTERN_POSITIVE_INTEGER)]
      })
    });
  }

  addAvailableValue(): void {
    this.availableValues.push(this.createAvailableValueFormItem());
  }

  removeAvailableValue(i: number) {
    this.availableValues.removeAt(i);
  }

  onSubmit() {
    if (this.attrForm.valid) {
      this.formSubmitting = true;
      this.dictService.createDictionaryAttribute(this.attrForm.value).subscribe(
        (result) => {
          this.dialogRef.close(true);
          const mess = this.data.edition ? "Modification de l'attribut effectuée" : "Création de l'attribut effectuée";
          this.alertService.success(mess);
        },
        (err: HttpErrorResponse) => {
          this.dialogRef.close(false);
        }
      );
    }
  }
  onNoClick(): void {
    this.dialogRef.close(false);
  }
}
